import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import {
  BarElement,
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  Filler,
  LinearScale,
  PointElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./BarChart.module.scss";

Chart.register(CategoryScale, LinearScale, PointElement, BarElement, Filler);

export type BarChartData = { label: string; data: { label: string; value: number | null }[] }[];

export interface BarChartProps extends VisualizationProps {
  data: BarChartData;
  stacked?: boolean;
  showLegend?: boolean;
  orientation?: "horizontal" | "vertical";
  xAxisTitle?: string;
  yAxisTitle?: string;
}

export default function BarChart(props: BarChartProps) {
  const {
    data,
    loading,
    stacked,
    showLegend = true,
    orientation = "vertical",
    xAxisTitle,
    yAxisTitle,
  } = props;
  const { formatLabel, colorPalette, lightColorPalette } = useVisualization(props);

  const chartData: ChartData<"bar", (number | null)[], string> = {
    labels: data.length > 0 ? data[0].data.map((row) => row.label) : [],
    datasets: data.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data.map((row) => row.value),
      backgroundColor: lightColorPalette[index % data.length],
      borderColor: colorPalette[index % data.length],
      fill: stacked === true,
    })),
  };

  const options: ChartOptions<"bar"> = {
    indexAxis: orientation === "horizontal" ? "y" : "x",
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => formatLabel(context.raw),
        },
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: { text: xAxisTitle || "", display: !!xAxisTitle },
      },
      y: {
        title: { text: yAxisTitle || "", display: !!yAxisTitle },
        suggestedMin: 0,
        stacked: stacked === true,
      },
    },
    interaction: {
      intersect: false,
      mode: "nearest",
      axis: orientation === "horizontal" ? "y" : "x",
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  const sum = data.reduce((a, b) => a + b.data.reduce((a, b) => a + (b.value || 0), 0), 0);

  return (
    <div className={styles.BarChart}>
      {!loading && (data.length === 0 || sum === 0) ? (
        <NoDataPoints />
      ) : (
        <>
          {showLegend && (
            <div className={styles.legend}>
              {loading
                ? [...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={styles["legend-row"]}
                    >
                      <Skeleton style={{ width: 80, height: 14 }} />
                    </div>
                  ))
                : data.map((row, index) => {
                    return (
                      <div
                        key={"legend" + index}
                        className={styles["legend-row"]}
                      >
                        <div className={styles["legend-header"]}>
                          <span
                            className={styles["legend-color"]}
                            style={{
                              background: lightColorPalette[index % lightColorPalette.length],
                              borderColor: colorPalette[index % colorPalette.length],
                            }}
                          ></span>
                          <span className={styles["legend-label"]}>{row.label}</span>
                        </div>
                      </div>
                    );
                  })}
            </div>
          )}
          <div className={styles.chart}>
            {loading ? (
              <Skeleton
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <Bar
                data={chartData}
                options={options}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
